import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Mic, MicOff, Volume2, VolumeX, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Bonjour ! Je suis l'assistant IA de Studio Raneem. Je peux vous aider a creer des devis, factures, rechercher des clients, et bien plus. Comment puis-je vous aider ?"
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    // Generate session ID on mount
    setSessionId(`session_${Date.now()}`);
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendTextMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) throw new Error('Erreur de communication');

      const data = await response.json();
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response,
        action: data.action
      }]);

      // Handle action result
      if (data.action) {
        handleActionResult(data.action);
      }

      // Play audio response if enabled
      if (audioEnabled && data.audio_base64) {
        playAudio(data.audio_base64);
      }

    } catch (error) {
      toast.error("Erreur lors de la communication avec l'assistant");
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Desolee, une erreur s'est produite. Veuillez reessayer."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActionResult = (action) => {
    if (action.success && action.data) {
      if (action.name === 'create_quote' || action.name === 'create_invoice') {
        toast.success(action.message, {
          action: {
            label: 'Voir',
            onClick: () => window.location.href = action.data.url
          }
        });
      } else if (action.name === 'search_customer' && action.data.customers?.length > 0) {
        // Show found customers
        toast.info(`${action.data.customers.length} client(s) trouve(s)`);
      }
    }
  };

  const playAudio = (base64Audio) => {
    try {
      setIsSpeaking(true);
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audioRef.current = audio;
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = () => setIsSpeaking(false);
      audio.play();
    } catch (error) {
      setIsSpeaking(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await sendVoiceMessage(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      toast.error("Impossible d'acceder au microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendVoiceMessage = async (audioBlob) => {
    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: '🎤 Message vocal...' }]);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('session_id', sessionId);

      const response = await fetch(`${API}/api/ai-assistant/chat-voice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Erreur de communication');

      const data = await response.json();

      // Update the voice message with transcription would require backend change
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'user', content: '🎤 Message vocal' };
        return [...updated, { 
          role: 'assistant', 
          content: data.response,
          action: data.action
        }];
      });

      if (data.action) {
        handleActionResult(data.action);
      }

      // Auto-play response
      if (audioEnabled && data.audio_base64) {
        playAudio(data.audio_base64);
      }

    } catch (error) {
      toast.error("Erreur lors de l'envoi du message vocal");
    } finally {
      setIsLoading(false);
    }
  };

  const clearConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/ai-assistant/session/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setSessionId(`session_${Date.now()}`);
      setMessages([{
        role: 'assistant',
        content: "Conversation effacee. Comment puis-je vous aider ?"
      }]);
    } catch (error) {
      // Silent fail
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendTextMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-800' 
            : 'bg-[#26788A] hover:bg-[#006060]'
        }`}
        data-testid="ai-assistant-toggle"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <MessageCircle size={24} className="text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 h-[500px] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#26788A] to-[#005555] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle size={18} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Assistant IA</h3>
                <p className="text-white/70 text-xs">Toujours pret a vous aider</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title={audioEnabled ? "Desactiver le son" : "Activer le son"}
              >
                {audioEnabled ? (
                  <Volume2 size={16} className="text-white" />
                ) : (
                  <VolumeX size={16} className="text-white/50" />
                )}
              </button>
              <button
                onClick={clearConversation}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title="Effacer la conversation"
              >
                <Trash2 size={16} className="text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-[#26788A] text-white rounded-br-md'
                      : 'bg-white text-slate-800 shadow-sm border border-slate-100 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.action && msg.action.success && msg.action.data?.url && (
                    <a 
                      href={msg.action.data.url}
                      className="mt-2 inline-block text-xs underline text-[#26788A] hover:text-[#005555]"
                    >
                      Voir le document
                    </a>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-slate-100 rounded-bl-md">
                  <Loader2 size={18} className="animate-spin text-[#26788A]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-slate-200">
            <div className="flex items-center gap-2">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isLoading}
                className={`p-2.5 rounded-full transition-all ${
                  isRecording 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                title={isRecording ? "Arreter l'enregistrement" : "Enregistrer un message vocal"}
              >
                {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
              </button>
              
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ecrivez votre message..."
                disabled={isLoading || isRecording}
                className="flex-1 border-slate-200 focus:border-[#26788A] focus:ring-[#26788A]"
              />
              
              <button
                onClick={sendTextMessage}
                disabled={isLoading || !inputText.trim() || isRecording}
                className="p-2.5 bg-[#26788A] text-white rounded-full hover:bg-[#006060] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={18} />
              </button>
            </div>
            
            {isSpeaking && (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                <div className="flex gap-0.5">
                  <span className="w-1 h-3 bg-[#26788A] rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                  <span className="w-1 h-3 bg-[#26788A] rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                  <span className="w-1 h-3 bg-[#26788A] rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                </div>
                <span>En train de parler...</span>
                <button onClick={stopAudio} className="text-red-500 hover:text-red-600">
                  Arreter
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
